import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TButtonSize, TButtonTheme } from '@cian/ui-kit/button/types';
import { IconCommunicationMessage16 } from '@cian/ui-kit-design-tokens/icons';
import { CianNewbuildingChatButton, CianNewbuildingChatButtonContext } from '@cian/newbuilding-chat-component';

import { useChatButtonContextValue } from './hooks';
import { useBuilderChat } from '../../hooks/useBuilderChat';
import { getBuilderChatEnabled } from '../../selectors/builderChat';
import { setBuilderChatButtonClicked } from '../../slices/builderChat';
import { ChatOnboardingContainer } from '../ChatOnboardingContainer';
import { INewbuildingBuilder } from '../../types/newbuilding/builder';
import { INewbuildingSeller } from '../../types/newbuilding/seller';
import { getNewbuildingId } from '../../selectors/newbuilding';
import { getGALabel } from '../../selectors/newbuilding/getGALabel';
import { trackContactListOpen } from './tracking';

interface IBuilderChatButtonContainerProps {
  asIcon?: boolean;
  buttonSize?: TButtonSize;
  buttonTheme?: TButtonTheme;
  withOnboarding?: boolean;
  placeType: string;
  forcedContacts?: {
    builders: INewbuildingBuilder[];
    sellers: INewbuildingSeller[];
  };
}

export const BuilderChatButtonContainer: React.FC<IBuilderChatButtonContainerProps> = ({
  asIcon = false,
  buttonSize = 'M',
  buttonTheme = 'fill_primary',
  withOnboarding = false,
  placeType,
  forcedContacts,
}) => {
  const isChatEnabled = useSelector(getBuilderChatEnabled);
  const chatButtonContextValue = useChatButtonContextValue(forcedContacts);
  const newbuildingId = useSelector(getNewbuildingId);
  const gaLabel = useSelector(getGALabel);

  const dispatch = useDispatch();

  const handleButtonClick = React.useCallback(() => {
    dispatch(setBuilderChatButtonClicked());
  }, [dispatch]);

  const handleContactListModalOpen = React.useCallback(() => {
    trackContactListOpen(newbuildingId, gaLabel);
  }, [newbuildingId, gaLabel]);

  const handleSellersInfoClick = React.useCallback(() => {}, []);

  const sellerType = React.useMemo(() => {
    if (!forcedContacts) {
      return undefined;
    }

    return forcedContacts.builders.length > 0 ? 'builders' : 'agents';
  }, [forcedContacts]);

  const { openChat } = useBuilderChat(placeType, sellerType);

  if (!isChatEnabled) {
    return null;
  }

  const beforeIcon = (
    <IconCommunicationMessage16
      color={buttonTheme === 'fill_primary' ? 'icon-inverted-default' : 'icon-main-default'}
    />
  );

  const messageButton = asIcon ? (
    <CianNewbuildingChatButton
      aria-label="Написать"
      beforeIcon={beforeIcon}
      fullWidth
      onClick={handleButtonClick}
      onChatOpen={openChat}
      size={buttonSize}
      theme={buttonTheme}
      onContactListModalOpen={handleContactListModalOpen}
      onSellersInfoClick={handleSellersInfoClick}
    />
  ) : (
    <CianNewbuildingChatButton
      beforeIcon={beforeIcon}
      fullWidth
      onClick={handleButtonClick}
      onChatOpen={openChat}
      size={buttonSize}
      theme={buttonTheme}
      onContactListModalOpen={handleContactListModalOpen}
      onSellersInfoClick={handleSellersInfoClick}
    >
      Написать
    </CianNewbuildingChatButton>
  );

  const contextualizedMessageButton = (
    <CianNewbuildingChatButtonContext.Provider value={chatButtonContextValue}>
      {messageButton}
    </CianNewbuildingChatButtonContext.Provider>
  );

  if (withOnboarding) {
    return <ChatOnboardingContainer type="builder">{contextualizedMessageButton}</ChatOnboardingContainer>;
  }

  return contextualizedMessageButton;
};
